import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import moment from 'moment';
import fileDownload from 'js-file-download';
import { getTeams } from 'src/services/projectService';
import {
  attendanceErrorMessage,
  exportAttendanceGrid,
  getMyAttendance
} from 'src/services/lmsAttendanceService';
import SearchableSelect from './SearchableSelect';
import {
  DirectoryEmployee,
  TeamMemberRef,
  employeesForTeam,
  teamMembersFromRoles
} from './attendanceOptions';

export type AttendanceEmployee = {
  id: string;
  name: string;
  department: string;
  matchIds?: string[];
};

export type AttendanceProject = {
  code: string;
  name: string;
};

type DownloadReportDialogProps = {
  open: boolean;
  employees: AttendanceEmployee[];
  projects: AttendanceProject[];
  departmentOptions: string[];
  showEmployeeFilter: boolean;
  showDepartmentFilter: boolean;
  showProjectFilter: boolean;
  limitToListedEmployees?: boolean;
  onClose: () => void;
};

const DownloadReportDialog = ({
  open,
  employees,
  projects,
  departmentOptions,
  showEmployeeFilter,
  showDepartmentFilter,
  showProjectFilter,
  limitToListedEmployees = false,
  onClose
}: DownloadReportDialogProps) => {
  const [start, setStart] = useState(() => moment().startOf('day'));
  const [end, setEnd] = useState(() => moment().startOf('day'));
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [department, setDepartment] = useState('all');
  const [projectCode, setProjectCode] = useState('all');
  const [teamMembers, setTeamMembers] = useState<TeamMemberRef[]>([]);
  const [loadingProject, setLoadingProject] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  const employeeOptions = useMemo(() => {
    const directory = employees as DirectoryEmployee[];
    const source =
      projectCode !== 'all' ? employeesForTeam(teamMembers, directory) : employees;
    return source.filter(
      (employee) =>
        department === 'all' ||
        employee.department.toLowerCase() === department.toLowerCase()
    );
  }, [projectCode, employees, teamMembers, department]);

  const onProjectChange = (code: string) => {
    setProjectCode(code);
    setEmployeeIds([]);
    if (code === 'all') {
      setTeamMembers([]);
      return;
    }

    setLoadingProject(true);
    getTeams(code)
      .then((res) => {
        const roles = Array.isArray(res?.data) ? res.data : [];
        setTeamMembers(teamMembersFromRoles(roles));
      })
      .catch(() => setTeamMembers([]))
      .finally(() => setLoadingProject(false));
  };

  const download = () => {
    const rangeStart = start.isAfter(end) ? end : start;
    const rangeEnd = start.isAfter(end) ? start : end;
    const fromDate = rangeStart.format('YYYY-MM-DD');
    const toDate = rangeEnd.format('YYYY-MM-DD');
    setDownloading(true);
    setDownloadError('');

    const request = showEmployeeFilter
      ? exportAttendanceGrid({
          fromDate,
          toDate,
          employees: employeeIds.length
            ? employeeIds
            : projectCode !== 'all' || limitToListedEmployees
            ? employeeOptions.map((employee) => employee.id).filter(Boolean)
            : undefined,
          departments: !showDepartmentFilter || department === 'all' ? undefined : [department]
        }).then((res) => {
          if (res?.status !== 200 || !res.data) {
            throw new Error('Unable to download the attendance report.');
          }
          fileDownload(res.data, 'attendance.xlsx');
        })
      : getMyAttendance(fromDate, toDate).then((res) => {
          const days = Array.isArray(res?.data?.data?.days) ? res.data.data.days : null;
          if (!days) {
            throw new Error('Unable to download the attendance report.');
          }
          const header = ['Date', 'In time', 'Status'];
          const csv = [
            header,
            ...days.map((day) => [
              day.date,
              day.inTime || '',
              day.holiday && !day.onLeave
                ? [day.holidayType || 'Holiday', day.holidaySession].filter(Boolean).join(' - ')
                : day.display || (day.onLeave ? day.leaveType || 'Leave' : '')
            ])
          ]
            .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');
          fileDownload(csv, `attendance-${fromDate}-to-${toDate}.csv`);
        });

    request
      .then(() => onClose())
      .catch((error) => {
        setDownloadError(attendanceErrorMessage(error, 'Unable to download the attendance report.'));
      })
      .finally(() => setDownloading(false));
  };

  const fieldRadius = {
    '& .MuiOutlinedInput-root': {
      borderRadius: (theme) => theme.general.borderRadius
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: (theme) => theme.general.borderRadius,
          overflow: 'visible'
        }
      }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: (theme) => theme.general.borderRadius,
              bgcolor: 'primary.lighter',
              color: 'primary.main'
            }}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </Box>
          <Typography variant="h4" sx={{ flex: 1 }}>
            Download Attendance Report
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="caption">Date Range</Typography>
        <DateRangePicker
          initialSettings={{
            startDate: start.toDate(),
            endDate: end.toDate(),
            locale: { format: 'DD MMM YYYY' }
          }}
          onApply={(_event, picker) => {
            setStart(moment(picker.startDate).startOf('day'));
            setEnd(moment(picker.endDate).startOf('day'));
          }}
        >
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 0.75, ...fieldRadius }}
            value={`${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`}
            inputProps={{ readOnly: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              )
            }}
          />
        </DateRangePicker>

        {showProjectFilter && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption">Project</Typography>
            <Box sx={{ mt: 0.75 }}>
              <SearchableSelect
                value={projectCode}
                allLabel="All Projects"
                options={projects.map((project) => ({
                  id: project.code,
                  label: project.name
                }))}
                onChange={onProjectChange}
              />
            </Box>
          </Box>
        )}

        {(showEmployeeFilter || showDepartmentFilter) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm:
                  showEmployeeFilter && showDepartmentFilter
                    ? 'minmax(0, 1fr) minmax(0, 1fr)'
                    : 'minmax(0, 1fr)'
              },
              gap: 2,
              mt: 2
            }}
          >
            {showEmployeeFilter && (
              <Box>
                <Typography variant="caption">Employee Selection</Typography>
                <Box sx={{ mt: 0.75 }}>
                  <SearchableSelect
                    multiple
                    value={employeeIds.filter((id) =>
                      employeeOptions.some((employee) => employee.id === id)
                    )}
                    allLabel="All Employees"
                    options={employeeOptions.map((employee) => ({
                      id: employee.id,
                      label: employee.name
                    }))}
                    onChange={setEmployeeIds}
                  />
                </Box>
              </Box>
            )}
            {showDepartmentFilter && (
              <Box>
                <Typography variant="caption">Department</Typography>
                <FormControl fullWidth size="small" sx={{ mt: 0.75, ...fieldRadius }}>
                  <Select
                    value={department}
                    onChange={(event) => {
                      setDepartment(event.target.value);
                      setEmployeeIds([]);
                    }}
                  >
                    <MenuItem value="all">All Departments</MenuItem>
                    {departmentOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudDownloadOutlinedIcon />}
            onClick={download}
            disabled={loadingProject || downloading}
          >
            Download Report
          </Button>
        </Box>
        {downloadError && (
          <Typography variant="body2" color="error" sx={{ mt: 1.5, textAlign: 'right' }}>
            {downloadError}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default DownloadReportDialog;
